/**
 * Reverse solicitation: when a Canadian MSB may deal with someone in the EU.
 *
 * A Canadian money services business is not licensed to provide services into
 * the EU, and may not market, target, advertise or offer them there. It may
 * serve an EU-based client only where that client approached it entirely on
 * their own initiative — the "reverse solicitation" exemption.
 *
 * The exemption is narrow and it is the FIRM that has to be able to prove it.
 * So the declaration is not a tick-box we file and forget: the applicant states
 * in their own words what prompted them to make contact and what they are
 * asking for, and that statement is kept with the application. A regulator
 * asking "how did this client reach you" gets the client's own answer, dated,
 * rather than our assurance.
 *
 * Two consequences worth stating, because they shape the UI as much as the law:
 *
 *  - The declaration must be REQUIRED before an EU applicant can continue.
 *    Collecting it afterwards would mean the relationship began without it.
 *  - Nothing around it may read as an offer. No "welcome", no encouragement to
 *    proceed, no suggestion that the service is available to them — the wording
 *    describes what the applicant is asserting, and nothing more.
 *
 * The list is EU member states plus the EEA (Iceland, Liechtenstein, Norway),
 * which the same restriction reaches. It is data, and it changes: keep the
 * source and date together when compliance revises it.
 *
 * Source: EU member states as at 2026-09; EEA per the EEA Agreement.
 */

/** EU member states. */
const EU_MEMBER_STATES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
] as const;

/**
 * EEA states that are not EU members.
 *
 * The same prohibition on marketing into the territory reaches them, so an
 * applicant here needs the same declaration. Treating them as "not EU" would be
 * a distinction the rule does not make.
 */
const EEA_NON_EU = ['IS', 'LI', 'NO'] as const;

const RESTRICTED: ReadonlySet<string> = new Set<string>([...EU_MEMBER_STATES, ...EEA_NON_EU]);

/**
 * Whether an applicant in this country may be dealt with only on a
 * reverse-solicitation basis.
 *
 * An unknown or empty country returns false rather than true: this gate blocks
 * an application from proceeding, and blocking somebody because we could not
 * read their country would be a bug that looks like compliance. The country is
 * required elsewhere in the form, so an empty one is caught there.
 */
export function requiresReverseSolicitation(country: string | null | undefined): boolean {
  if (country === null || country === undefined) return false;
  return RESTRICTED.has(country.trim().toUpperCase());
}

/** The countries the rule reaches, for a back-office reader or a test. */
export const REVERSE_SOLICITATION_COUNTRIES: readonly string[] = [...RESTRICTED].sort();

/** The shortest statement we will accept as an answer rather than a shrug. */
export const MIN_STATEMENT_LENGTH = 20;

/**
 * Whether a declaration is complete enough to rely on.
 *
 * Both parts are required. The confirmation alone is a tick nobody can defend
 * to a regulator; the statement is the part that evidences how the client
 * actually arrived.
 */
export function isReverseSolicitationComplete(input: {
  readonly confirmed?: boolean;
  readonly statement?: string;
}): boolean {
  return input.confirmed === true && (input.statement ?? '').trim().length >= MIN_STATEMENT_LENGTH;
}
